import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as turf from '@turf/turf';
import PropTypes from 'prop-types';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useMapEvents } from 'react-leaflet';
import { useDispatch, useSelector } from 'react-redux';
import ActiveCustomerIcon from '../../../../assets/img/activeCustomerPin.png';
import { DropdownControl, LoaderControl } from '../../../../components/LeafletMap';
import { dictionary } from '../../../../lang';
import { MAP_LEGEND, MAP_TYPE } from '../../../../lib/constants';
import { getTeamCells, getTeamPins, selectTeamCells, selectTeamPins } from '../../../../redux';
import { booleanPolygonsIntersects } from '../../../../utils';
import { TeamMapContext } from '..';
import { getOutcomePinIcon, parsePinOutcome } from '../helpers';
import { HeatMap, WeeksSinceKnocked } from './HeatMap';
import PixiOverlay from './PixiOverlay';
const TeamChoropleth = ({ teamId, selectedPolygon, setViewBoundary, }) => {
    const dispatch = useDispatch();
    const [pixiLoaded, setPixiLoaded] = useState(true);
    const [dropdownLabel, setDropdownLabel] = useState(dictionary.QUALIFIED_ADDRESSES);
    const { heatMap, setHeatMap, pinMode, setPinMode, displayPins, isPinsScoreMode, displayPinsOutcomes, } = useContext(TeamMapContext);
    const { teamCells, isLoading: isTeamCellsLoading } = useSelector(selectTeamCells);
    const { teamPins } = useSelector(selectTeamPins);
    const pixiOverlayMarkers = useMemo(() => {
        if (!pinMode) {
            return [];
        }
        const mapType = isPinsScoreMode ? heatMap : MAP_TYPE.QUALIFIED_ADDRESSES;
        const buildTeamMarkers = (mapType, selectedPolygon) => {
            const markers = [];
            teamPins.forEach((thisPin) => {
                let iconStyles;
                if (selectedPolygon?.pins?.includes(thisPin.id)) {
                    const { color, knockTimes, hasNote, type } = parsePinOutcome(thisPin.outcomeType);
                    const pinScoreStyles = MAP_LEGEND.OUTCOME_PINS
                        .find((item) => item.color === color + (knockTimes ? `-${knockTimes}` : ''));
                    iconStyles = {
                        iconStyles: pinScoreStyles,
                        iconId: thisPin.outcomeType,
                        customIcon: thisPin.isActive ?
                            ActiveCustomerIcon :
                            getOutcomePinIcon({ type, color, hasNote, ...pinScoreStyles }),
                    };
                }
                else {
                    switch (mapType) {
                        case MAP_TYPE.PIN_SCORE:
                            const pinScoreStyles = MAP_LEGEND.QUALIFIED_AREAS_PINS
                                .find((item) => item.quartile === (thisPin.qualifiedAreaQuartile ?? 0));
                            iconStyles = { iconStyles: pinScoreStyles };
                            break;
                        default:
                            const defaultStyles = MAP_LEGEND.TEAM_PINS
                                .find((item) => item.isQualified === Boolean(thisPin.isQualified));
                            iconStyles = {
                                iconStyles: defaultStyles,
                                iconId: thisPin.isActive ? 'activeCustomer' : null,
                                customIcon: thisPin.isActive ? ActiveCustomerIcon : null,
                            };
                    }
                }
                markers.push({
                    id: thisPin.id,
                    position: thisPin.position,
                    ...iconStyles,
                });
            });
            return markers;
        };
        return displayPinsOutcomes
            ? buildTeamMarkers(mapType, selectedPolygon)
            : displayPins
                ? buildTeamMarkers(mapType)
                : [];
    }, [
        teamPins,
        displayPins,
        displayPinsOutcomes,
        heatMap,
        isPinsScoreMode,
        pinMode,
        selectedPolygon,
    ]);
    const dropdownOptions = [
        {
            key: MAP_TYPE.QUALIFIED_ADDRESSES,
            label: dictionary.QUALIFIED_ADDRESSES,
            onClick: () => {
                setDropdownLabel(dictionary.QUALIFIED_ADDRESSES);
                setHeatMap(MAP_TYPE.QUALIFIED_ADDRESSES);
            },
        },
        {
            key: MAP_TYPE.WEEKS_SINCE_KNOCKED,
            label: dictionary.WEEKS_SINCE_KNOCKED,
            onClick: () => {
                setDropdownLabel(dictionary.WEEKS_SINCE_KNOCKED);
                setHeatMap(MAP_TYPE.WEEKS_SINCE_KNOCKED);
            },
        },
        {
            key: MAP_TYPE.PIN_SCORE,
            label: dictionary.PIN_SCORE,
            onClick: () => {
                setDropdownLabel(dictionary.PIN_SCORE);
                setHeatMap(MAP_TYPE.PIN_SCORE);
            },
        },
        {
            key: MAP_TYPE.NONE,
            label: dictionary.NONE,
            onClick: () => {
                setDropdownLabel(dictionary.NONE);
                setHeatMap(MAP_TYPE.NONE);
            },
        },
    ];
    const map = useMapEvents({
        dragend: () => {
            if (!map) {
                return;
            }
            if (!isTeamCellsLoading) {
                handleMapChange();
            }
        },
        zoomend: () => {
            if (!map) {
                return;
            }
            if (!isTeamCellsLoading) {
                handleMapChange();
            }
        },
    });
    const requestTeamCells = useCallback(() => {
        dispatch(getTeamCells.request({
            teamId,
            resolution: 8,
        }));
    }, [
        dispatch,
        teamId,
    ]);
    const requestTeamPins = useCallback((pinRequestBoundary) => {
        dispatch(getTeamPins.request({
            teamId,
            boundaries: pinRequestBoundary,
        }));
    }, [
        dispatch,
        teamId,
    ]);
    const getBoundsWindow = useCallback(() => {
        const bounds = map.getBounds();
        const northWest = bounds.getNorthWest();
        const northEast = bounds.getNorthEast();
        const southEast = bounds.getSouthEast();
        const southWest = bounds.getSouthWest();
        return [
            [
                [northEast.lng, northEast.lat],
                [southEast.lng, southEast.lat],
                [southWest.lng, southWest.lat],
                [northWest.lng, northWest.lat],
                [northEast.lng, northEast.lat],
            ],
        ];
    }, [map]);
    const handleMapChange = useCallback(() => {
        const viewBounds = turf.polygon(getBoundsWindow());
        let totalAddresses = 0;
        const viewableCells = [];
        if (teamCells?.features.length) {
            teamCells.features.forEach((thisCell) => {
                const intersects = booleanPolygonsIntersects(thisCell.geometry, viewBounds);
                if (intersects) {
                    viewableCells.push(thisCell);
                    totalAddresses += thisCell.properties.qualified_addresses;
                }
            });
        }
        if (totalAddresses <= 5000 && viewableCells.length) {
            setPinMode(true);
            const combinedCells = viewableCells.reduce((acc, curr) => turf.union(acc, curr));
            const pinRequestBoundary = turf.intersect(viewBounds, combinedCells);
            setViewBoundary(pinRequestBoundary.geometry);
            requestTeamPins(pinRequestBoundary.geometry);
        }
        else {
            setPinMode(false);
        }
    }, [
        teamCells,
        getBoundsWindow,
        requestTeamPins,
        setPinMode,
        setViewBoundary,
    ]);
    useEffect(() => {
        requestTeamCells();
    }, [requestTeamCells]);
    return (_jsxs("div", { children: [(!pixiLoaded) && _jsx(LoaderControl, {}), _jsx(PixiOverlay, { markers: pixiOverlayMarkers, setLoading: setPixiLoaded }), !pinMode && !isTeamCellsLoading && (_jsxs("div", { children: [_jsx(DropdownControl, { label: dropdownLabel, options: dropdownOptions }), heatMap === MAP_TYPE.QUALIFIED_ADDRESSES && _jsx(HeatMap, { mapType: MAP_TYPE.QUALIFIED_ADDRESSES }), heatMap === MAP_TYPE.WEEKS_SINCE_KNOCKED && _jsx(WeeksSinceKnocked, {}), heatMap === MAP_TYPE.PIN_SCORE && _jsx(HeatMap, { mapType: MAP_TYPE.PIN_SCORE })] }))] }));
};
TeamChoropleth.propTypes = {
    teamId: PropTypes.number,
    selectedPolygon: PropTypes.object,
    setViewBoundary: PropTypes.func,
};
export default TeamChoropleth;
